/**
 * B2B-prisopløsning — den EKSAKTE 4-trins-rækkefølge fra spec §4.2:
 *
 *   1. Firma-variantpris (company_variant_prices)            → 'company_override'
 *   2. Laveste pakkepris blandt firmaets prispakker           → 'package'
 *   3. Rabat-% (største matchende COLLECTION-rabat, ellers
 *      firmaets default_discount_pct) på listepris ex moms    → 'discount'
 *   4. Listepris ÷ 1,25                                       → 'list'
 *
 * Ren logik — al data gives som argumenter. Ingen React/Supabase/I/O.
 */

import { exVat } from "./vat";

/** Række fra company_variant_prices (pris er ex moms). */
export interface CompanyVariantPrice {
  shopifyVariantId: string;
  /** Pris ex moms. */
  price: number;
  minOrderQty: number;
  packSize: number;
}

/** Trin-pris fra package_variant_price_tiers (stykpris ex moms fra min_qty). */
export interface QuantityPriceTier {
  minQty: number;
  /** Stykpris ex moms. */
  price: number;
}

/** Trin-rabat fra package_access_discount_tiers (procent fra min_qty). */
export interface QuantityDiscountTier {
  minQty: number;
  discountPct: number;
}

/** Række fra package_variant_prices (pris er ex moms). */
export interface PackagePrice {
  packageId?: string;
  shopifyVariantId: string;
  /** Pris ex moms (grundpris — gælder fra antal 1). */
  price: number;
  minOrderQty: number;
  packSize: number;
  /** Mængde-trin: stykpris fra min_qty (erstatter grundprisen ved antal >= min_qty). */
  tiers?: readonly QuantityPriceTier[];
}

/** Række fra company_product_access / package_product_access. */
export interface AccessRule {
  accessType: "PRODUCT" | "COLLECTION";
  shopifyProductId?: string | null;
  shopifyCollectionId?: string | null;
  discountPct: number;
  /** Mængde-trin: rabat-% fra min_qty. Effektiv rabat = max(grundrabat, bedste trin). */
  discountTiers?: readonly QuantityDiscountTier[];
}

export type PriceSource = "company_override" | "package" | "discount" | "list";

export interface ResolveVariantPriceInput {
  shopifyVariantId: string;
  /** Shopify-listepris INKL. moms (rå variant.price). */
  listPriceInclVat: number;
  /** Firmaets variantpris-overrides (company_variant_prices). */
  companyVariantPrices?: readonly CompanyVariantPrice[];
  /** Union af package_variant_prices for ALLE firmaets tildelte pakker. */
  packagePrices?: readonly PackagePrice[];
  /** Firmaets company_product_access-regler. */
  accessRules?: readonly AccessRule[];
  /** Id'er på de kollektioner produktet indgår i (til COLLECTION-rabat-match). */
  productCollectionIds?: readonly string[];
  /** companies.default_discount_pct. */
  defaultDiscountPct?: number;
  /**
   * Antal styk af varianten (bruges til trin-priser og trin-rabatter).
   * Default 1 — uden antal opfører funktionen sig som før.
   */
  quantity?: number;
  /**
   * Mix & match: samlet antal i kurven pr. kollektion (kollektions-id → antal).
   * Bruges KUN til trin-RABATTER (en palle må gerne være blandede farver).
   * Mangler kollektionen i mappet, bruges `quantity`.
   */
  collectionQuantities?: ReadonlyMap<string, number>;
}

export interface ResolvedVariantPrice {
  /** Effektiv B2B-pris ex moms (uafrundet). */
  priceExVat: number;
  source: PriceSource;
  minOrderQty: number;
  packSize: number;
  /** Listepris ex moms (listePrisInklMoms ÷ 1,25). */
  listPriceExVat: number;
  /** Besparelse ift. listepris ex moms i procent, aldrig negativ. */
  savingsPct: number;
}

/** Heltal ≥ 1 (ugyldige/manglende værdier → 1). */
function sanitizeQty(value: number | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 1) return 1;
  return Math.floor(value);
}

/** Klemmer en rabatprocent ind i [0; 100]; ugyldig → 0. */
function sanitizeDiscountPct(value: number | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return 0;
  return Math.min(value, 100);
}

function isValidPrice(value: number): boolean {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

/** Effektiv pakkepris ved et givet antal: bedste trin (højeste min_qty <= antal), ellers grundpris. */
export function effectivePackagePrice(p: PackagePrice, quantity: number): number {
  const qty = sanitizeQty(quantity);
  let best = p.price;
  let bestMinQty = 0;
  for (const tier of p.tiers ?? []) {
    const minQty = sanitizeQty(tier.minQty);
    if (minQty > qty || !isValidPrice(tier.price)) continue;
    if (minQty > bestMinQty) {
      bestMinQty = minQty;
      best = tier.price;
    }
  }
  return best;
}

/** Effektiv rabat-% for en regel ved et givet antal: max(grundrabat, bedste opnåede trin). */
function effectiveRulePct(rule: AccessRule, quantity: number): number {
  const qty = sanitizeQty(quantity);
  let best = sanitizeDiscountPct(rule.discountPct);
  for (const tier of rule.discountTiers ?? []) {
    const minQty = sanitizeQty(tier.minQty);
    if (minQty > qty) continue;
    const pct = sanitizeDiscountPct(tier.discountPct);
    if (pct > best) best = pct;
  }
  return best;
}

/**
 * Største matchende COLLECTION-rabat for et produkt:
 * max(discount_pct) over firmaets COLLECTION-regler hvis kollektion indeholder
 * produktet. 0 hvis ingen matcher.
 */
export function resolveCollectionDiscountPct(
  accessRules: readonly AccessRule[],
  productCollectionIds: readonly string[],
  quantity = 1,
  collectionQuantities?: ReadonlyMap<string, number>,
): number {
  let best = 0;
  for (const rule of accessRules) {
    if (rule.accessType !== "COLLECTION") continue;
    if (!rule.shopifyCollectionId) continue;
    if (!productCollectionIds.includes(rule.shopifyCollectionId)) continue;
    const ruleQty = collectionQuantities?.get(rule.shopifyCollectionId) ?? quantity;
    const pct = effectiveRulePct(rule, ruleQty);
    if (pct > best) best = pct;
  }
  return best;
}

/**
 * Opløs den effektive B2B-pris for en variant efter 4-trins-hierarkiet.
 * Kaster RangeError ved negativ/ugyldig listepris.
 */
export function resolveVariantPrice(input: ResolveVariantPriceInput): ResolvedVariantPrice {
  const {
    shopifyVariantId,
    listPriceInclVat,
    companyVariantPrices = [],
    packagePrices = [],
    accessRules = [],
    productCollectionIds = [],
    defaultDiscountPct = 0,
    quantity = 1,
    collectionQuantities,
  } = input;
  const qty = sanitizeQty(quantity);

  if (!isValidPrice(listPriceInclVat)) {
    throw new RangeError(`Ugyldig listepris (inkl. moms): ${listPriceInclVat}`);
  }

  const listPriceExVat = exVat(listPriceInclVat);

  const finish = (
    priceExVat: number,
    source: PriceSource,
    minOrderQty: number,
    packSize: number,
  ): ResolvedVariantPrice => ({
    priceExVat,
    source,
    minOrderQty,
    packSize,
    listPriceExVat,
    savingsPct:
      listPriceExVat > 0 ? Math.max(0, (1 - priceExVat / listPriceExVat) * 100) : 0,
  });

  // 1. Firma-variantpris (override)
  const companyPrice = companyVariantPrices.find(
    (p) => p.shopifyVariantId === shopifyVariantId && isValidPrice(p.price),
  );
  if (companyPrice) {
    return finish(
      companyPrice.price,
      "company_override",
      sanitizeQty(companyPrice.minOrderQty),
      sanitizeQty(companyPrice.packSize),
    );
  }

  // 2. Laveste pakkepris på tværs af alle tildelte pakker (trin-priser medregnet)
  let bestPackage: PackagePrice | null = null;
  let bestPackagePrice = Number.POSITIVE_INFINITY;
  for (const p of packagePrices) {
    if (p.shopifyVariantId !== shopifyVariantId || !isValidPrice(p.price)) continue;
    const effective = effectivePackagePrice(p, qty);
    if (!bestPackage || effective < bestPackagePrice) {
      bestPackage = p;
      bestPackagePrice = effective;
    }
  }
  if (bestPackage) {
    return finish(
      bestPackagePrice,
      "package",
      sanitizeQty(bestPackage.minOrderQty),
      sanitizeQty(bestPackage.packSize),
    );
  }

  // 3. Rabat-%: største matchende COLLECTION-rabat (trin medregnet), ellers firmaets default
  const collectionPct = resolveCollectionDiscountPct(
    accessRules,
    productCollectionIds,
    qty,
    collectionQuantities,
  );
  const discountPct = collectionPct > 0 ? collectionPct : sanitizeDiscountPct(defaultDiscountPct);
  if (discountPct > 0) {
    return finish(listPriceExVat * (1 - discountPct / 100), "discount", 1, 1);
  }

  // 4. Listepris ex moms
  return finish(listPriceExVat, "list", 1, 1);
}

/** Pristrin til visning ("1+ / 10+ / 50+ / 400+"). */
export interface PriceBreak {
  minQty: number;
  /** Effektiv stykpris ex moms ved dette antal. */
  priceExVat: number;
  savingsPct: number;
}

/**
 * Beregn synlige pristrin for en variant: alle relevante min_qty-tærskler
 * (pakke-trin for varianten + rabat-trin for matchende kollektionsregler),
 * evalueret gennem resolveVariantPrice. Trin uden prisændring filtreres fra.
 * Returnerer [] hvis der ikke findes mængdetrin (kun én pris).
 */
export function priceBreaks(input: ResolveVariantPriceInput): PriceBreak[] {
  const thresholds = new Set<number>([1]);
  for (const p of input.packagePrices ?? []) {
    if (p.shopifyVariantId !== input.shopifyVariantId) continue;
    for (const t of p.tiers ?? []) thresholds.add(Math.max(1, Math.floor(t.minQty)));
  }
  const collectionIds = input.productCollectionIds ?? [];
  for (const rule of input.accessRules ?? []) {
    if (rule.accessType !== "COLLECTION" || !rule.shopifyCollectionId) continue;
    if (!collectionIds.includes(rule.shopifyCollectionId)) continue;
    for (const t of rule.discountTiers ?? []) thresholds.add(Math.max(1, Math.floor(t.minQty)));
  }
  if (thresholds.size <= 1) return [];

  const sorted = [...thresholds].sort((a, b) => a - b);
  const breaks: PriceBreak[] = [];
  for (const minQty of sorted) {
    const resolved = resolveVariantPrice({
      ...input,
      quantity: minQty,
      collectionQuantities: undefined,
    });
    const prev = breaks[breaks.length - 1];
    if (prev && Math.abs(prev.priceExVat - resolved.priceExVat) < 0.005) continue;
    breaks.push({ minQty, priceExVat: resolved.priceExVat, savingsPct: resolved.savingsPct });
  }
  return breaks.length > 1 ? breaks : [];
}

/**
 * Mix & match: samlet antal pr. kollektion for en kurv.
 * productCollections: produkt-id → kollektions-id'er.
 */
export function computeCollectionQuantities(
  lines: readonly { productId: string; quantity: number }[],
  productCollections: ReadonlyMap<string, readonly string[]>,
): Map<string, number> {
  const result = new Map<string, number>();
  for (const line of lines) {
    const qty = sanitizeQty(line.quantity);
    for (const collectionId of productCollections.get(line.productId) ?? []) {
      result.set(collectionId, (result.get(collectionId) ?? 0) + qty);
    }
  }
  return result;
}
